import React, { type CSSProperties, useEffect } from 'react'
import { Card, CardContent, Typography, Stack, Grid, useTheme } from '@mui/material'
import { useSpring, animated } from '@react-spring/web'
import { red } from '@mui/material/colors'
import { type IBook } from '../../../models/book/book-types'
import { replaceExchangeShortname } from '../../../utils/exchange-utils'
import { formatToLocalMilliTimestampFromUTCString } from '../../../utils/time-utils'
import { getProductTypeColour } from '../../../utils/colours-utils'

interface BookCardProps {
  style: CSSProperties
  data: IBook
}

const BookCard: React.FunctionComponent<BookCardProps> = (props) => {
  const { time, symbol, bid, ask } = props.data

  const theme = useTheme()
  const [springs, api] = useSpring(() => ({
    from: {
      border: '8px solid ' + theme.palette.background.paper
    }
  }))
  useEffect(() => {
    const dataTime = new Date(time)
    const timeDiff = Date.now() - dataTime.getTime()
    let isMounted = true
    let timeoutId: ReturnType<typeof setTimeout>
    if (timeDiff > 5000) {
      api.start({
        from: {
          border: '8px solid ' + theme.palette.background.paper
        },
        to: {
          border: '8px solid ' + red[900]
        },
        loop: true,
        config: { tension: 120, friction: 14 }
      })
    } else {
      timeoutId = setTimeout(() => {
        if (isMounted) {
          api.start({
            from: {
              border: '8px solid ' + theme.palette.background.paper
            },
            to: {
              border: '8px solid ' + red[900]
            },
            loop: true,
            config: { tension: 120, friction: 14 }
          })
        }
      }, 5000)
    }

    return () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId)
      }
      api.stop()
      isMounted = false
    }
  }, [])

  const formattedDateTime = formatToLocalMilliTimestampFromUTCString(time)
  const formattedDate = formattedDateTime.slice(0, 10)
  const formattedTime = formattedDateTime.slice(11)

  return (
     <Card variant='outlined' sx={{ ...props.style, Right: 1 }}>
      <animated.div style={{ ...springs }}>
        <CardContent>
          <Stack direction='row' justifyContent='space-between'>
            <Typography variant='subtitle1' fontWeight='bold' sx={{ color: getProductTypeColour(symbol) }}>
              {replaceExchangeShortname(symbol)}
            </Typography>
            <Stack>
              <Typography variant='caption' align='right' lineHeight={1}>
                {formattedDate}
              </Typography>
              <Typography variant='caption' align='right' lineHeight={1}>
                {formattedTime}
              </Typography>
            </Stack>
          </Stack>
          <Grid container>
            <Grid item xs>
              <Typography align='left'>
                {bid}
              </Typography>
            </Grid>
            <Grid item xs>
              <Typography align='left'>
                {ask}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </animated.div>
    </Card>
  )
}

export default BookCard
